// src/hooks/useWebPush.ts
// Hook React per gestire l'iscrizione Web Push (Fase 3).
//
// Espone stato (isSupported/permission/subscription) e azioni
// (subscribe/unsubscribe) per integrare facilmente UI di consenso.
import { useCallback, useEffect, useState } from 'react';

import {
  getVapidPublicKey,
  registerSubscription,
  unregisterSubscription,
  urlBase64ToUint8Array,
} from '../services/webpush';

export type WebPushPermission = NotificationPermission;

export interface UseWebPushReturn {
  /** L'API è supportata dal browser (Service Worker + PushManager). */
  isSupported: boolean;
  /** Permesso Notification corrente: 'default' | 'granted' | 'denied'. */
  permission: WebPushPermission;
  /** Subscription locale corrente o null. */
  subscription: PushSubscription | null;
  /** True quando isSupported && permission==='granted' && subscription!==null. */
  isSubscribed: boolean;
  /** Richiede permesso e crea+registra la subscription. Idempotente. */
  subscribe: () => Promise<PushSubscription | null>;
  /** Cancella locale e remoto. No-op se non iscritto. */
  unsubscribe: () => Promise<void>;
}

function detectSupport(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function useWebPush(): UseWebPushReturn {
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [permission, setPermission] = useState<WebPushPermission>('default');
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);

  // Detect support + recupero subscription esistente al mount
  useEffect(() => {
    const supported = detectSupport();
    setIsSupported(supported);
    if (!supported) return;
    setPermission(Notification.permission);
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscription(sub || null))
      .catch((err) => {
        console.error('[useWebPush] errore getSubscription:', err);
      });
  }, []);

  // Polling stato permission (Fase 7 — gestione revoke da settings browser)
  useEffect(() => {
    if (!isSupported) return;
    const interval = window.setInterval(async () => {
      const curr = Notification.permission;
      if (curr !== permission) {
        setPermission(curr);
        // Se il permesso è stato revocato e abbiamo una subscription locale,
        // smontiamo tutto (sia locale sia remoto): senza permesso il push
        // non verrà mostrato comunque.
        if (curr === 'denied' && subscription) {
          try {
            await subscription.unsubscribe();
            await unregisterSubscription(subscription.endpoint);
          } catch (err) {
            console.warn('[useWebPush] cleanup post-revoke fallito:', err);
          }
          setSubscription(null);
        }
      }
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [isSupported, permission, subscription]);

  const subscribe = useCallback(async (): Promise<PushSubscription | null> => {
    if (!detectSupport()) return null;

    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      setSubscription(existing);
      // Re-sync col backend (idempotente lato server)
      try {
        await registerSubscription(existing);
      } catch (err) {
        console.warn('[useWebPush] re-sync subscription fallito:', err);
      }
      return existing;
    }

    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== 'granted') return null;

    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) {
      console.error('[useWebPush] VAPID public key vuota — controlla settings backend');
      return null;
    }

    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Critical: deve essere Uint8Array (stringa → InvalidAccessError su Chrome)
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    try {
      await registerSubscription(sub);
    } catch (err) {
      // Se il backend rifiuta, evitiamo di mantenere uno stato locale "ghost"
      console.error('[useWebPush] register al backend fallito, rollback locale:', err);
      try { await sub.unsubscribe(); } catch { /* noop */ }
      return null;
    }

    setSubscription(sub);
    return sub;
  }, []);

  const unsubscribe = useCallback(async (): Promise<void> => {
    const reg = detectSupport() ? await navigator.serviceWorker.ready : null;
    const current = subscription || (reg ? await reg.pushManager.getSubscription() : null);
    if (!current) return;
    const endpoint = current.endpoint;
    try {
      await current.unsubscribe();
    } catch (err) {
      console.warn('[useWebPush] unsubscribe locale fallito:', err);
    }
    try {
      await unregisterSubscription(endpoint);
    } catch (err) {
      console.warn('[useWebPush] DELETE backend fallito:', err);
    }
    setSubscription(null);
  }, [subscription]);

  const isSubscribed =
    isSupported && permission === 'granted' && subscription !== null;

  return {
    isSupported,
    permission,
    subscription,
    isSubscribed,
    subscribe,
    unsubscribe,
  };
}
